import type { Answers } from "../funnel/answers";
import { logAction, type LogTarget } from "./actionLog";

/**
 * ═══ CE QU'UNE ÉCRITURE DE LA PARTICIPATION RACONTE ═══
 *
 * La même écriture dit trois choses selon ce qui était enregistré avant elle :
 *
 *   « ne vient pas » : une désinscription (8), qu'on refuse la première fois ou qu'on annule ;
 *   on vient, et l'on n'était pas inscrit : l'inscription (7), « EXPOSANT · présentiel » ;
 *   on vient, et l'on l'était déjà : une modification (41), avec les étapes enregistrées ;
 *   sauf la seule typologie, la page « Mon profil » : la fiche demande « Modification ·
 *   Utilisateur CRM » (32), comme pour les informations.
 *
 * ⚠️ « AVANT » SE LIT AVANT L'ÉCRITURE. Après, le cache porte la nouvelle réponse, et
 * toute première inscription passerait pour une modification.
 */
const MODE_WORD: Record<string, string> = {
  onsite: "présentiel",
  remote: "à distance",
  represented: "représenté",
};

/** Les étapes dont les réponses partent avec la participation, dans l'ordre du tunnel. */
const STEP_WORD: ReadonlyArray<[keyof Answers, string]> = [
  ["participation", "Participation"],
  ["typoId", "Typologie"],
  ["days", "Dates"],
  ["articles", "Billetterie"],
];

export function stepsOf(answers: Answers): string[] {
  return STEP_WORD.filter(([key]) => answers[key] !== undefined).map(([, word]) => word);
}

export type ParticipationLog =
  | { action: "registration" | "registrationUpdate"; label: string }
  | { action: "unregistration" | "userUpdate" };

export function participationLog(
  wasRegistered: boolean,
  answer: string | undefined,
  details: { typo?: string; steps: string[] },
): ParticipationLog | null {
  if (answer === "decline") return { action: "unregistration" };
  if (!answer || !(answer in MODE_WORD)) return null;
  if (!wasRegistered) {
    const label = [details.typo, MODE_WORD[answer]].filter(Boolean).join(" · ");
    return { action: "registration", label };
  }
  if (details.steps.length === 1 && details.steps[0] === "Typologie") return { action: "userUpdate" };
  return { action: "registrationUpdate", label: details.steps.join(", ") };
}

export type ParticipationFacts = {
  wasRegistered: boolean;
  answer: string | undefined;
  typo?: string;
  steps: string[];
  /**
   * L'écriture a-t-elle changé quelque chose chez le serveur (`participationChanged`) ? `false`,
   * rien ne se journalise ; inconnu (`undefined`), on note, comme avant.
   */
  changed?: boolean;
};

/**
 * ═══ RIEN DE JOURNALISÉ SANS CHANGEMENT (bug de la chasse du 2026-10-20) ═══
 *
 * ⚠️ MESURÉ : l'action 41 partait à chaque passage par le tunnel sans rien changer, et au
 * rechargement de la confirmation. Une première correction marquait la chose écrite dans
 * `sessionStorage` : elle ne valait que dans un onglet, et un onglet neuf (ou une autre
 * machine) journalisait encore une modification qui n'avait pas eu lieu.
 *
 * La question se pose maintenant au SERVEUR, à travers l'écriture : ce qui part est comparé à
 * ce qu'il détenait, lu avant elle (`participationChanged`, dans `funnel/participant.ts`).
 * Rien de changé, rien au journal, quel que soit l'onglet. Le rechargement de la confirmation
 * n'a plus besoin de marque : il réécrit ce que le serveur vient d'enregistrer, donc rien.
 */
/**
 * ⚠️ LES FAITS SE LISENT ICI, DANS LE `try`, ET NON CHEZ L'APPELANT. Il note juste après
 * avoir écrit, dans la suite de son écriture : une lecture qui lèverait là ferait passer
 * une inscription enregistrée pour une écriture ratée, ou couperait la navigation qui suit.
 */
export function logParticipation(
  target: LogTarget | null | undefined,
  read: () => ParticipationFacts,
): void {
  try {
    const facts = read();
    const entry = participationLog(facts.wasRegistered, facts.answer, facts);
    if (!entry) return;
    if (facts.changed === false) return;
    if ("label" in entry) logAction(target, entry.action, { label: entry.label });
    else logAction(target, entry.action);
  } catch {
    /* le journal se tait : voir `log/actionLog.ts` */
  }
}
